import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, Button, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import LabelIcon from '@mui/icons-material/Label';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import ModernSection from '../../DFMPage/components/ModernSection/ModernSection';
import {
  useEditCompanyMutation,
  useCreateUpdateKeywordMutation,
  useCreateKeywordsMutation,
  useDeleteKeywordMutation,
  useGetKeywordsForCompanyQuery,
} from '../../../gql/generated/graphql';
import { decodeHtmlEntities } from '../../../utils/htmlEntityDecoder';

interface BBCompanyOverviewProps {
  companyId: string;
  companyName?: string;
  legalName?: string;
  street?: string;
  zipCode?: string;
  city?: string;
  bbFocusDescription?: string;
}

const BB_FOCUS_SOURCE = 'b&b-focus';

export const BBCompanyOverview = ({
  companyId,
  companyName,
  legalName,
  street,
  zipCode,
  city,
  bbFocusDescription,
}: BBCompanyOverviewProps) => {
  const { t } = useTranslation();
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState(decodeHtmlEntities(bbFocusDescription) || '');
  const [newKeyword, setNewKeyword] = useState('');
  const [isAddingKeyword, setIsAddingKeyword] = useState(false);

  const [editCompanyMutation, { loading: editLoading }] = useEditCompanyMutation();
  const [createUpdateKeyword, { loading: createKeywordLoading }] = useCreateUpdateKeywordMutation();
  const [createKeywords, { loading: createKeywordsLoading }] = useCreateKeywordsMutation();
  const [deleteKeyword, { loading: deleteKeywordLoading }] = useDeleteKeywordMutation();

  // Fetch keywords for this company
  const {
    data: keywordsData,
    loading: keywordsLoading,
    refetch: refetchKeywords,
  } = useGetKeywordsForCompanyQuery({
    variables: { companyId: parseInt(companyId) },
  });

  // Filter B&B focus keywords
  const bbKeywords =
    keywordsData?.getCompany?.edges
      ?.flatMap(edge => edge?.node?.crawlResults?.edges)
      ?.flatMap(crawlEdge => crawlEdge?.node?.xoviKeywords?.edges)
      ?.map(node => node?.node)
      ?.filter(keyword => keyword?.searchEngine === BB_FOCUS_SOURCE) || [];

  const genericCrawlResultId = keywordsData?.getCompany?.edges?.[0]?.node?.crawlResults?.edges?.[0]?.node?.dbId;

  useEffect(() => {
    setDescriptionValue(decodeHtmlEntities(bbFocusDescription) || '');
  }, [bbFocusDescription]);

  const handleSaveDescription = async () => {
    if (!companyId) return;

    try {
      await editCompanyMutation({
        variables: {
          companyId: parseInt(companyId),
          buyAndBuildFocus: descriptionValue,
        },
        refetchQueries: ['getCompanyForDFM'],
      });
      setIsEditingDescription(false);
    } catch (error) {
      console.error('Error updating B&B focus:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingDescription(false);
    setDescriptionValue(decodeHtmlEntities(bbFocusDescription) || '');
  };

  const handleAddKeyword = async () => {
    if (!genericCrawlResultId || !newKeyword.trim()) return;

    try {
      if (newKeyword.includes(',')) {
        // Multiple keywords
        const keywords = newKeyword
          .split(',')
          .map(k => k.trim())
          .filter(k => k);

        await createKeywords({
          variables: {
            genericCrawlResultId,
            keywords,
            searchEngine: BB_FOCUS_SOURCE,
          },
        });
      } else {
        // Single keyword
        await createUpdateKeyword({
          variables: {
            genericCrawlResultId,
            keyword: newKeyword.trim(),
            searchEngine: BB_FOCUS_SOURCE,
          },
        });
      }

      await refetchKeywords();
      setNewKeyword('');
      setIsAddingKeyword(false);
    } catch (error) {
      console.error('Error adding B&B keyword:', error);
    }
  };

  const handleDeleteKeyword = async (keywordId: number) => {
    try {
      await deleteKeyword({
        variables: {
          keywordId,
        },
      });
      await refetchKeywords();
    } catch (error) {
      console.error('Error deleting B&B keyword:', error);
    }
  };

  const subtitle = [legalName || companyName, street, zipCode, city].filter(Boolean).join(' - ');

  return (
    <ModernSection
      title={t('buyAndBuild.companyOverview', { defaultValue: 'Company Overview' })}
      subtitle={subtitle}
      icon={BusinessIcon}
      collapsible
      defaultExpanded={true}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        {/* Buy and Build Focus Description */}
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <BusinessIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="medium">
              Buy and Build Focus
            </Typography>
            {!isEditingDescription && (
              <Tooltip title="Edit B&B focus">
                <IconButton size="small" onClick={() => setIsEditingDescription(true)} sx={{ p: 0.3 }}>
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {isEditingDescription ? (
            <Box display="flex" flexDirection="column" gap={1}>
              <TextField
                multiline
                rows={4}
                value={descriptionValue}
                onChange={e => setDescriptionValue(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Describe the buy and build focus for this company..."
              />
              <Box display="flex" gap={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelEdit}
                  startIcon={<CloseIcon />}
                  disabled={editLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveDescription}
                  startIcon={editLoading ? <CircularProgress size={14} /> : <SaveIcon />}
                  disabled={editLoading}
                >
                  Save
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              p={2}
              borderRadius={2}
              border={1}
              borderColor="grey.100"
              minHeight={100}
              maxHeight={200}
              overflow="auto"
              sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {decodeHtmlEntities(bbFocusDescription) || 'No buy and build focus description available'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Buy and Build Topics */}
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <LabelIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="medium">
              Buy and Build Topics
            </Typography>
            {!isAddingKeyword && (
              <Tooltip title="Add topic">
                <IconButton size="small" onClick={() => setIsAddingKeyword(true)} sx={{ p: 0.3 }}>
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {isAddingKeyword && (
            <Box display="flex" gap={1} mb={2}>
              <TextField
                size="small"
                fullWidth
                value={newKeyword}
                onChange={e => setNewKeyword(e.target.value)}
                placeholder="Enter topic (or multiple topics separated by comma)..."
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    handleAddKeyword();
                  }
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleAddKeyword}
                disabled={!newKeyword.trim() || createKeywordLoading || createKeywordsLoading}
                startIcon={createKeywordLoading || createKeywordsLoading ? <CircularProgress size={14} /> : <AddIcon />}
              >
                Add
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setIsAddingKeyword(false);
                  setNewKeyword('');
                }}
              >
                Cancel
              </Button>
            </Box>
          )}

          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {keywordsLoading ? (
              <CircularProgress size={20} />
            ) : bbKeywords.length > 0 ? (
              bbKeywords.map(keyword => (
                <Chip
                  key={keyword?.dbId}
                  label={keyword?.keyword}
                  size="small"
                  variant="outlined"
                  onDelete={() => keyword?.dbId && handleDeleteKeyword(keyword.dbId)}
                  deleteIcon={
                    deleteKeywordLoading ? <CircularProgress size={14} /> : <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  }
                  sx={{ fontSize: '0.7rem' }}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No buy and build topics defined yet. Click the + button to add topics.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </ModernSection>
  );
};
